import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSales } from '../../context/SalesContext';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';

export default function SalesMenu() {
  const sales = useSales();
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    allShops,
    menuByShopId,
    addToCart,
    setCartQuantity,
    cartItemsWithDetails,
    clearCart,
    loading,
    loadError,
  } = sales;

  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderNo, setOrderNo] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [readyByTime, setReadyByTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [loadingOrderNo, setLoadingOrderNo] = useState(false);

  // Build full "today at selected time" for API when user picks time only
  const readyByDateTime = useMemo(() => {
    if (!readyByTime || !/^\d{2}:\d{2}$/.test(readyByTime)) return undefined;
    const [h, m] = readyByTime.split(':').map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d.toISOString();
  }, [readyByTime]);

  const shop = useMemo(() => (allShops.length > 0 ? allShops[0] : null), [allShops]);
  const menuItems = useMemo(
    () => (shop ? (menuByShopId[shop.id] || []) : []),
    [shop, menuByShopId]
  );
  useEffect(() => {
    if (shop && !sales.selectedShop) sales.setSelectedShop(shop);
  }, [shop, sales]);
  const categories = useMemo(() => {
    const set = new Set(menuItems.map((i) => i.category || 'Best Sellers'));
    return Array.from(set).filter((c) => c !== 'Best Sellers').sort();
  }, [menuItems]);

  const filteredItems = useMemo(() => {
    let list = menuItems;
    if (selectedCategory) {
      list = list.filter((i) => (i.category || 'Best Sellers') === selectedCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (i) =>
          (i.name && i.name.toLowerCase().includes(q)) ||
          (i.description && i.description.toLowerCase().includes(q))
      );
    }
    return list;
  }, [menuItems, selectedCategory, search]);

  const cartTotal = cartItemsWithDetails.reduce((a, i) => a + i.price * i.quantity, 0);
  const cartCount = cartItemsWithDetails.reduce((a, i) => a + i.quantity, 0);

  useEffect(() => {
    if (showOrderForm && !orderNo) {
      setLoadingOrderNo(true);
      apiService.orders
        .generateSalesNumber()
        .then((res) => setOrderNo(res.data?.orderNumber || ''))
        .catch(() => setOrderNo(''))
        .finally(() => setLoadingOrderNo(false));
    }
  }, [showOrderForm, orderNo]);

  const openOrderForm = () => {
    if (cartCount === 0) return;
    setShowOrderForm(true);
    setSubmitError('');
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (!customerName.trim()) {
      setSubmitError('Customer name is required.');
      return;
    }
    setSubmitError('');
    setSubmitting(true);
    try {
      await apiService.orders.createSales({
        orderNo: orderNo || undefined,
        shopName: shop?.name || 'Grill n Rice',
        userName: user?.name || 'Sales User',
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim() || undefined,
        readyByTime: readyByDateTime,
        items: cartItemsWithDetails.map((i) => ({
          id: i.id,
          name: i.name,
          quantity: i.quantity,
          category: i.category,
          price: i.price,
        })),
      });
      clearCart();
      setShowOrderForm(false);
      setCustomerName('');
      setCustomerPhone('');
      setReadyByTime('');
      setOrderNo('');
      navigate('/sales/dashboard', { state: { message: 'Order sent successfully.' } });
    } catch (err) {
      setSubmitError(err.response?.data?.error || err.message || 'Failed to submit order.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">{loadError}</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">Loading menu…</div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">No shop or menu available.</div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 pb-28 sm:pb-24">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-blue-500 p-3 sm:p-4 md:p-6">
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
          <button
            type="button"
            onClick={() => navigate('/sales/dashboard')}
            className="text-gray-600 hover:text-gray-800 p-2 -ml-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-gray-100 touch-manipulation"
            aria-label="Back"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg sm:text-xl font-bold text-gray-700 truncate">{shop.name}</h1>
        </div>

        <div className="relative mb-3 sm:mb-4">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
          <input
            type="text"
            placeholder="Search menu items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-3 sm:py-2.5 text-base text-gray-700 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1 scrollbar-thin snap-x snap-mandatory">
          <button
            type="button"
            onClick={() => setSelectedCategory('')}
            className={`flex-shrink-0 snap-start min-h-[44px] px-4 py-2.5 rounded-lg text-sm font-medium border touch-manipulation ${
              !selectedCategory ? 'bg-blue-500 text-white border-blue-500' : 'bg-gray-100 text-gray-700 border-gray-200'
            }`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setSelectedCategory(cat)}
              className={`flex-shrink-0 snap-start min-h-[44px] px-4 py-2.5 rounded-lg text-sm font-medium border touch-manipulation ${
                selectedCategory === cat ? 'bg-blue-500 text-white border-blue-500' : 'bg-gray-100 text-gray-700 border-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {filteredItems.map((item) => {
            const qty = cartItemsWithDetails.find((c) => c.id === item.id)?.quantity || 0;
            return (
              <div
                key={item.id}
                className="flex items-center gap-3 sm:gap-4 p-3 rounded-lg border border-gray-200 bg-gray-50"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-800 text-base sm:text-sm">{item.name}</div>
                  {item.description && (
                    <div className="text-sm text-gray-500 truncate">{item.description}</div>
                  )}
                  <div className="text-lg sm:text-base font-semibold text-gray-800 mt-0.5">
                    SAR {Number(item.price).toFixed(2)}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {qty > 0 ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setCartQuantity(item.id, qty - 1)}
                        className="min-w-[44px] min-h-[44px] w-11 h-11 rounded-full bg-blue-500 text-white flex items-center justify-center text-lg font-medium hover:bg-blue-600 touch-manipulation"
                      >
                        −
                      </button>
                      <span className="w-8 text-center font-medium text-gray-800 text-base">{qty}</span>
                      <button
                        type="button"
                        onClick={() => addToCart(item.id)}
                        className="min-w-[44px] min-h-[44px] w-11 h-11 rounded-full bg-blue-500 text-white flex items-center justify-center text-lg font-medium hover:bg-blue-600 touch-manipulation"
                      >
                        +
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => addToCart(item.id)}
                      className="min-w-[44px] min-h-[44px] w-11 h-11 rounded-full bg-blue-500 text-white flex items-center justify-center text-xl font-medium hover:bg-blue-600 touch-manipulation"
                    >
                      +
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {cartCount > 0 && (
        <div className="fixed left-4 right-4 md:left-auto md:right-6 md:max-w-sm z-30 bottom-4 md:bottom-6" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <button
            type="button"
            onClick={openOrderForm}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 shadow-lg text-base min-h-[48px] touch-manipulation"
          >
            <span>🛒</span>
            <span>VIEW CART ({cartCount} items) - SAR {cartTotal.toFixed(2)}</span>
          </button>
        </div>
      )}

      {showOrderForm && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50"
          onClick={() => !submitting && setShowOrderForm(false)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-h-[90vh] sm:max-h-none overflow-y-auto max-w-md sm:mx-4 p-4 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-gray-800 mb-4">Order details</h2>
            <form onSubmit={handleSubmitOrder} className="space-y-4">
              <div className="pb-3 border-b border-gray-200">
                <div className="text-sm font-medium text-gray-700 mb-2">Items in this order</div>
                <ul className="max-h-40 overflow-y-auto space-y-1.5 bg-gray-50 rounded-lg p-3 text-sm">
                  {cartItemsWithDetails.map((i) => (
                    <li key={i.id} className="flex justify-between gap-2">
                      <span className="text-gray-800">{i.name} × {i.quantity}</span>
                      <span className="font-medium text-gray-800 tabular-nums">SAR {(i.price * i.quantity).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex justify-between text-sm font-semibold text-gray-800 mt-2">
                  <span>Total</span>
                  <span className="tabular-nums">SAR {cartTotal.toFixed(2)}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order number</label>
                <input
                  type="text"
                  value={orderNo}
                  onChange={(e) => setOrderNo(e.target.value)}
                  placeholder={loadingOrderNo ? 'Generating…' : 'e.g. SALES-DDMMYYYY-001'}
                  disabled={loadingOrderNo}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer name *</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Customer name"
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone number</label>
                <input
                  type="text"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="Phone number"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time to give order back (today only)</label>
                <input
                  type="time"
                  value={readyByTime}
                  onChange={(e) => setReadyByTime(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500 [&::-webkit-calendar-picker-indicator]:opacity-100"
                />
              </div>
              {submitError && (
                <div className="p-2 bg-red-100 text-red-700 text-sm rounded">{submitError}</div>
              )}
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg font-medium"
                >
                  {submitting ? 'Submitting…' : 'Submit order'}
                </button>
                <button
                  type="button"
                  onClick={() => !submitting && setShowOrderForm(false)}
                  disabled={submitting}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
